import type {
  CreateSmsTemplateData,
  DeleteSmsTemplateData,
  DeleteSmsTemplatePayload,
  GetSendingHistoryData,
  GetSendingHistoryParams,
  GetSmsBasicTemplateByData,
  GetSmsTemplateListData,
  GetSmsTemplateSampleData,
  GetSmsTemplateSampleParams,
  Pageable,
  SendSmsByTemplateData,
  SendSmsData,
  SmsDirectSendingRequest,
  SmsSampleRequest,
  SmsSearchRequest,
  SmsTemplateRequest,
  SmsTemplateSendingRequest,
  UpdateSmsTemplateData,
} from '~/api-spec/__generated__/main'
import { httpClient, type RequestMeta } from '~/http/client'
import type { Slice } from '~/schema/slice'

import {
  adaptSmsHistory,
  adaptSmsTemplate,
  adaptSmsTemplates,
  adaptSmsTemplateSampleMessage,
} from './adapters'
import type { SmsHistory, SmsTemplate, SmsTemplateSendingType } from './schemas'

// 2026-10-14 백엔드 develop(6a0f8e7bb) 의 sms/controller/SmsController.kt·request/ 로 확인.
// 생성 타입은 조회 조건(SmsSearchRequest·SmsSampleRequest)을 객체 하나로 그리지만 실제 쿼리는 그 필드를 평탄하게 받는다.

/***************** 발송 기록 *****************/

export type SmsHistoryParams = SmsSearchRequest &
  Pick<GetSendingHistoryParams, 'studentIdList'> &
  Pageable

/** 기간·받는 사람·학생으로 거른 발송 기록과 상태별 건수 */
export const getSmsHistory = async (
  params: SmsHistoryParams,
  meta?: RequestMeta,
): Promise<SmsHistory> => {
  const body = await httpClient().get<GetSendingHistoryData>('/sms', { params, meta })
  return adaptSmsHistory(body.data)
}

/***************** 발송 *****************/
// 발송은 서버가 접수만 하고(202) 응답 본문이 없다.

export type SmsDirectSendBody = SmsDirectSendingRequest

export const sendSms = async (body: SmsDirectSendBody, meta?: RequestMeta): Promise<void> => {
  await httpClient().post<SendSmsData>('/sms/direct', body, { meta })
}

export type SmsTemplateSendBody = SmsTemplateSendingRequest

/** 템플릿 내용에 학생 정보를 채워 보낸다 */
export const sendSmsByTemplate = async (
  body: SmsTemplateSendBody,
  meta?: RequestMeta,
): Promise<void> => {
  await httpClient().post<SendSmsByTemplateData>('/sms', body, { meta })
}

/***************** 템플릿 *****************/

export const getSmsTemplates = async (
  params?: Pageable,
  meta?: RequestMeta,
): Promise<Slice<SmsTemplate>> => {
  const body = await httpClient().get<GetSmsTemplateListData>('/sms/templates', { params, meta })
  return adaptSmsTemplates(body.data)
}

/** 용도별 기본 템플릿. 응답은 CommonResponse 봉투 없이 본문 그대로다 */
export const getSmsBasicTemplate = async (
  templateSendingType: SmsTemplateSendingType,
  meta?: RequestMeta,
): Promise<SmsTemplate> => {
  const template = await httpClient().get<GetSmsBasicTemplateByData>(
    '/sms/basic-templates/by-sending-type',
    { params: { templateSendingType }, meta },
  )
  return adaptSmsTemplate(template)
}

// id 는 서버가 읽지 않는다. 수정할 템플릿은 경로로 고른다.
export type SmsTemplateBody = Omit<SmsTemplateRequest, 'id'>

export const createSmsTemplate = async (
  body: SmsTemplateBody,
  meta?: RequestMeta,
): Promise<void> => {
  await httpClient().post<CreateSmsTemplateData>('/sms/templates', body, { meta })
}

export const updateSmsTemplate = async (
  id: SmsTemplate['id'],
  body: SmsTemplateBody,
  meta?: RequestMeta,
): Promise<void> => {
  await httpClient().patch<UpdateSmsTemplateData>(`/sms/templates/${id}`, body, { meta })
}

/** 기본(BASIC) 템플릿이 섞이면 서버가 오류로 응답한다 */
export const deleteSmsTemplates = async (
  ids: DeleteSmsTemplatePayload,
  meta?: RequestMeta,
): Promise<void> => {
  await httpClient().delete<DeleteSmsTemplateData>('/sms/templates', { data: ids, meta })
}

// 용도에 따라 채우는 조건이 다르다. 등·하원은 datetime, 결제 완료 안내는 tuitionFee·수업 기간, 보고서는 link 다.
export type SmsTemplateSampleParams = SmsSampleRequest &
  Pick<GetSmsTemplateSampleParams, 'studentIdList'>

/** 용도별 기본 템플릿에 조건을 채운 발송 미리보기 문구 */
export const getSmsTemplateSampleMessage = async (
  params: SmsTemplateSampleParams,
  meta?: RequestMeta,
): Promise<string> => {
  const body = await httpClient().get<GetSmsTemplateSampleData>('/sms/templates/samples', {
    params,
    meta,
  })
  return adaptSmsTemplateSampleMessage(body.data)
}
